import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import axiosInstance from "../Config/axios";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const PlayerAccountContext = createContext();
export const usePlayerAccount = () => useContext(PlayerAccountContext);

export const PlayerAccountProvider = ({ children }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toastRef = useRef(null);
  const navigate = useNavigate();

  const time_Zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const clearPlayers = () => setPlayers([]);

  const handleUnauthorized = () => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Access token missing. Please log in again.",
      confirmButtonColor: "#B79527", // orange / gold
      cancelButtonColor: "#1C1C1C",
    });
    clearPlayers();
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  /**  Fetch all players */
  const fetchPlayers = useCallback(async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get("/customer/player-account/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setPlayers(response.data || []);
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Failed to fetch players";
      setError(errorDetail);
      // toastRef.current?.show({
      //   severity: "error",
      //   summary: "Error",
      //   detail: errorDetail,
      //   life: 3000,
      // });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /**  Fetch parent players via sale_id */
  const fetchParentPlayers = useCallback(
    async (saleId) => {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) return handleUnauthorized();

      setLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.get(
          "/scheduler/list-parent-players/",
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { sale_id: saleId },
          },
        );
        return response.data?.players || [];
      } catch (err) {
        const errorDetail =
          err.response?.data?.detail || "Failed to fetch parent players";
        setError(errorDetail);

        return [];
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**  Create player */
  const createPlayer = async (playerData) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    const headers = { Authorization: `Bearer ${accessToken}` };
    if (!(playerData instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    try {
      const response = await axiosInstance.post(
        "/customer/player-account/",
        playerData,
        { headers },
      );
      await fetchPlayers();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Player created successfully",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: true, data: response.data };
    } catch (err) {
      const errorDetail = err.response?.data || {
        detail: "Failed to create player",
      };
      setError(errorDetail.detail);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorDetail,
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false, error: errorDetail };
    } finally {
      setLoading(false);
    }
  };

  /**  Edit player */
  const editPlayer = async (playerId, playerData) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    const headers = { Authorization: `Bearer ${accessToken}` };
    if (!(playerData instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    try {
      const response = await axiosInstance.patch(
        `/customer/player-account/?id=${playerId}`,
        playerData,
        { headers },
        { params: { timezone: time_Zone } },
      );
      await fetchPlayers();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Player updated successfully",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: true, data: response.data };
    } catch (err) {
      const errorDetail = err.response?.data || {
        detail: "Failed to update player",
      };
      setError(errorDetail.detail);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorDetail,
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false, error: errorDetail };
    } finally {
      setLoading(false);
    }
  };

  /**  Edit player */
  const deletePlayer = async (playerId) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      const response = await axiosInstance.delete(
        `/customer/player-account/?id=${playerId}`,
        { headers },
      );
      // await fetchPlayers();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Player deleted successfully",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: true, data: response.data };
    } catch (err) {
      const errorDetail = err.response?.data || {
        detail: "Failed to delete player",
      };
      setError(errorDetail.detail);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorDetail,
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false, error: errorDetail };
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayersPackages = useCallback(async (id) => {
    try {
      const response = await axiosInstance.get(
        `/customer/my-packages/?player_id=${id}`,
      );
      return response.data || [];
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Failed to fetch players";
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchParentPackages = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }
    setLoading(true);

    try {
      const response = await axiosInstance.get(`/customer/my-packages/`);
      return response.data || [];
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.message || "Failed to fetch packages.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (data) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    const headers = { Authorization: `Bearer ${accessToken}` };
    if (!(data instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    try {
      const response = await axiosInstance.post(
        "/payment/cancel-subscription/",
        data,
        { headers },
      );
      await fetchPlayers();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Subscription canceled successfully",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
      return { success: true, data: response.data };
    } catch (err) {
      const errorDetail = err.response?.data.message || {
        detail: "Failed to cancel subscription",
      };
      setError(errorDetail.detail);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorDetail,
        confirmButtonColor: "#B79527",
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false, error: errorDetail };
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }
    setLoading(true);

    try {
      const response = await axiosInstance.get(`/customer/payment-history/`);
      return response.data.sales || [];
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.message || "Failed to fetch packages.",
        confirmButtonColor: "#B79527", // orange / gold
        cancelButtonColor: "#1C1C1C",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PlayerAccountContext.Provider
      value={{
        players,
        loading,
        error,
        fetchPlayers,
        fetchParentPlayers,
        createPlayer,
        editPlayer,
        deletePlayer,
        fetchPlayersPackages,
        fetchParentPackages,
        cancelSubscription,
        fetchPaymentHistory,
      }}
    >
      <Toast ref={toastRef} />
      {children}
    </PlayerAccountContext.Provider>
  );
};
